import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-hot-toast';
import { FiPlus, FiSearch, FiEdit2, FiTrash2, FiInbox } from 'react-icons/fi';
import Button from '../../../component/button/button';
import Pagination from '../../../component/pagination/pagination';
import {
  ConfirmDeleteToast,
  LoaderComponent,
  limitPagination,
  successMessage,
  errorMessageShow,
} from '../../../utils/Helper';
import { listHierarchyItems, deleteHierarchyItem } from '../../../api/subject';
import {
  HierarchyItem,
  SubjectDetail,
  enabledDepth,
  labelAt,
  pluralize,
} from './hierarchyUtils';
import HierarchyAncestorFilters, {
  EMPTY_ANCESTOR_FILTERS,
  HierarchyAncestorFilterValues,
} from './HierarchyAncestorFilters';
import NodeModal from './NodeModal';

interface HierarchyTablePanelProps {
  subject: SubjectDetail;
  level: number;
  onItemsChanged?: () => void;
}

const HierarchyTablePanel: React.FC<HierarchyTablePanelProps> = ({
  subject,
  level,
  onItemsChanged,
}) => {
  const labels = subject.level_labels;
  const depth = enabledDepth(labels);
  const levelLabel = labelAt(labels, level);
  const parentLabel = level > 1 ? labelAt(labels, level - 1) : undefined;
  const hasChildLevel = level < depth;
  const childLabel = hasChildLevel ? labelAt(labels, level + 1) : undefined;

  const [items, setItems] = useState<HierarchyItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [ancestorFilters, setAncestorFilters] =
    useState<HierarchyAncestorFilterValues>(EMPTY_ANCESTOR_FILTERS);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [modalMode, setModalMode] = useState<'create' | 'edit' | null>(null);
  const [editing, setEditing] = useState<HierarchyItem | undefined>(undefined);

  useEffect(() => {
    const timer = window.setTimeout(() => setSearch(searchInput.trim()), 300);
    return () => window.clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    setPage(1);
  }, [search, ancestorFilters, level]);

  const loadItems = useCallback(async () => {
    try {
      setLoading(true);
      const response = await listHierarchyItems(subject.id, {
        level,
        search: search || undefined,
        level_1_id: ancestorFilters.level_1_id || undefined,
        level_2_id: ancestorFilters.level_2_id || undefined,
        level_3_id: ancestorFilters.level_3_id || undefined,
        page,
        limit: limitPagination,
      });
      const data = response?.data?.data;
      setItems(data?.items || []);
      setTotal(data?.total ?? 0);
    } catch (error) {
      errorMessageShow('Failed to load items.');
    } finally {
      setLoading(false);
    }
  }, [subject.id, level, search, ancestorFilters, page]);

  useEffect(() => {
    loadItems();
  }, [loadItems]);

  const handleDelete = (item: HierarchyItem) => {
    toast(t => (
      <ConfirmDeleteToast
        message={`Delete "${item.name}"${
          item.child_count > 0 ? ' and everything inside it' : ''
        }?`}
        onConfirm={async () => {
          toast.dismiss(t.id);
          try {
            await deleteHierarchyItem(subject.id, item.id);
            successMessage(`${levelLabel} deleted.`);
            loadItems();
            onItemsChanged?.();
          } catch (error) {
            errorMessageShow('Could not delete item.');
          }
        }}
        onCancel={() => toast.dismiss(t.id)}
      />
    ));
  };

  const onSaved = () => {
    loadItems();
    onItemsChanged?.();
  };

  const showingFrom = total === 0 ? 0 : (page - 1) * limitPagination + 1;
  const showingTo = Math.min(page * limitPagination, total);

  return (
    <div className="hierarchy_table_panel">
      <div className="hierarchy_table_toolbar">
        <div className="hierarchy_table_titles">
          <h3>{pluralize(levelLabel)}</h3>
          <span className="hierarchy_table_count">
            {total} {total === 1 ? 'item' : 'items'}
          </span>
        </div>

        <div className="hierarchy_table_actions">
          <div className="hierarchy_search">
            <FiSearch />
            <input
              value={searchInput}
              placeholder={`Search ${pluralize(levelLabel).toLowerCase()}…`}
              onChange={e => setSearchInput(e.target.value)}
            />
          </div>
          <Button
            text={`Add ${levelLabel}`}
            icon={<FiPlus />}
            buttonType="button"
            onClick={() => {
              setEditing(undefined);
              setModalMode('create');
            }}
            style={{ height: 42, fontSize: 14 }}
          />
        </div>
      </div>

      <HierarchyAncestorFilters
        subject={subject}
        level={level}
        values={ancestorFilters}
        onChange={setAncestorFilters}
      />

      {loading ? (
        <LoaderComponent label="Loading…" />
      ) : items.length === 0 ? (
        <div className="hierarchy_empty">
          <FiInbox />
          {total === 0 && !search ? (
            <>
              <p>No {pluralize(levelLabel).toLowerCase()} yet.</p>
              <span>Use the “Add {levelLabel}” button to create one.</span>
            </>
          ) : (
            <p>No matches for the current search or filters.</p>
          )}
        </div>
      ) : (
        <>
          <div className="hierarchy_table_wrap">
            <table className="hierarchy_table">
              <thead>
                <tr>
                  <th>{levelLabel}</th>
                  {level > 1 && <th>{parentLabel}</th>}
                  {hasChildLevel && <th className="num">{pluralize(childLabel!)}</th>}
                  <th className="actions_col">Actions</th>
                </tr>
              </thead>
              <tbody>
                {items.map(item => (
                  <tr key={item.id}>
                    <td className="name_col">{item.name}</td>
                    {level > 1 && (
                      <td className="parent_col">
                        {item.parent_name || (
                          <span className="muted">—</span>
                        )}
                      </td>
                    )}
                    {hasChildLevel && (
                      <td className="num">
                        <span className="hierarchy_count_badge">
                          {item.child_count}
                        </span>
                      </td>
                    )}
                    <td className="actions_col">
                      <button
                        className="hierarchy_icon_btn"
                        title="Edit"
                        onClick={() => {
                          setEditing(item);
                          setModalMode('edit');
                        }}
                      >
                        <FiEdit2 />
                      </button>
                      <button
                        className="hierarchy_icon_btn danger"
                        title="Delete"
                        onClick={() => handleDelete(item)}
                      >
                        <FiTrash2 />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="hierarchy_table_footer">
            <span className="hierarchy_table_range">
              Showing {showingFrom}–{showingTo} of {total}
            </span>
            {total > limitPagination && (
              <Pagination
                postsPerPage={limitPagination}
                length={total}
                currentPage={page}
                handlePagination={setPage}
              />
            )}
          </div>
        </>
      )}

      {modalMode && (
        <NodeModal
          subjectId={subject.id}
          level={level}
          levelLabel={levelLabel}
          parentLabel={parentLabel}
          mode={modalMode}
          item={editing}
          onClose={() => setModalMode(null)}
          onSaved={onSaved}
        />
      )}
    </div>
  );
};

export default HierarchyTablePanel;
